/**
 * MIT License
 *
 * Copyright (c) 2020 Excalidraw
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell\
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 */

/**
 * we have take this code from excalidraw library and made change according our need
 * npm package: https://www.npmjs.com/package/@excalidraw/excalidraw
 * github url: https://github.com/excalidraw/excalidraw
 */

import { ExcalidrawElement } from "@excalidraw/excalidraw/types/element/types";
import { AppState, BinaryFiles } from "@excalidraw/excalidraw/types/types";
import {
  BroadcastedExcalidrawElement,
  PRECEDING_ELEMENT_KEY,
} from "@/components/common/Whiteboard/Whiteboard.types";

// ref: https://github.com/excalidraw/excalidraw/blob/v0.15.0/src/utils.ts#L684
export const arrayToMapWithIndex = <T extends { id: string }>(
  elements: readonly T[],
) =>
  elements.reduce((acc, element: T, idx) => {
    acc.set(element.id, [element, idx]);
    return acc;
  }, new Map<string, [element: T, index: number]>());

// ref: https://github.com/excalidraw/excalidraw/blob/v0.15.0/src/excalidraw-app/collab/reconciliation.ts#L14
export const shouldDiscardRemoteElement = (
  localAppState: AppState,
  local: ExcalidrawElement | undefined,
  remote: ExcalidrawElement,
): boolean => {
  if (
    local &&
    // local element is being edited
    (local.id === localAppState.editingElement?.id ||
      local.id === localAppState.resizingElement?.id ||
      local.id === localAppState.draggingElement?.id ||
      // local element is newer
      local.version > remote.version ||
      // resolve conflicting edits deterministically by taking the one with
      // the lowest versionNonce
      (local.version === remote.version &&
        local.versionNonce < remote.versionNonce))
  ) {
    return true;
  }
  return false;
};

// ref: https://github.com/excalidraw/excalidraw/blob/v0.15.0/src/excalidraw-app/collab/reconciliation.ts#L37
export const reconcileElements = (
  localElements: readonly ExcalidrawElement[],
  remoteElements: readonly BroadcastedExcalidrawElement[],
  localAppState?: AppState,
) => {
  const reconciledElements: ExcalidrawElement[] = localElements.slice();
  const localElementsData =
    arrayToMapWithIndex<ExcalidrawElement>(localElements);
  const duplicates = new WeakMap<ExcalidrawElement, true>();
  let cursor = 0;
  let offset = 0;
  let remoteElementIdx = -1;
  // eslint-disable-next-line no-restricted-syntax
  for (const remoteElement of remoteElements) {
    remoteElementIdx += 1;

    const local = localElementsData.get(remoteElement.id);
    if (
      localAppState &&
      shouldDiscardRemoteElement(localAppState, local?.[0], remoteElement)
    ) {
      if (remoteElement[PRECEDING_ELEMENT_KEY]) {
        delete remoteElement[PRECEDING_ELEMENT_KEY];
      }
      // eslint-disable-next-line no-continue
      continue;
    }

    // Mark duplicate for removal as it'll be replaced with the remote element
    if (local) {
      // Unless the remote and local elements are the same element in which case
      // we need to keep it as we'd otherwise discard it from the resulting
      // array.
      if (local[0] === remoteElement) {
        // eslint-disable-next-line no-continue
        continue;
      }
      duplicates.set(local[0], true);
    }

    // parent may not be defined in case the remote client is running an older
    // excalidraw version
    const parent =
      (remoteElement[PRECEDING_ELEMENT_KEY] ??
        remoteElements[remoteElementIdx - 1]?.id) ||
      null;

    if (parent != null) {
      delete remoteElement[PRECEDING_ELEMENT_KEY];

      // ^ indicates the element is the first in elements array
      if (parent === "^") {
        offset += 1;
        if (cursor === 0) {
          reconciledElements.unshift(remoteElement);
          localElementsData.set(remoteElement.id, [
            remoteElement,
            cursor - offset,
          ]);
        } else {
          reconciledElements.splice(cursor + 1, 0, remoteElement);
          localElementsData.set(remoteElement.id, [
            remoteElement,
            cursor + 1 - offset,
          ]);
          cursor += 1;
        }
      } else {
        let idx = localElementsData.has(parent)
          ? localElementsData.get(parent)![1]
          : null;
        if (idx != null) {
          idx += offset;
        }
        if (idx != null && idx >= cursor) {
          reconciledElements.splice(idx + 1, 0, remoteElement);
          offset += 1;
          localElementsData.set(remoteElement.id, [
            remoteElement,
            idx + 1 - offset,
          ]);
          cursor = idx + 1;
        } else if (idx != null) {
          reconciledElements.splice(cursor + 1, 0, remoteElement);
          offset += 1;
          localElementsData.set(remoteElement.id, [
            remoteElement,
            cursor + 1 - offset,
          ]);
          cursor += 1;
        } else {
          reconciledElements.push(remoteElement);
          localElementsData.set(remoteElement.id, [
            remoteElement,
            reconciledElements.length - 1 - offset,
          ]);
        }
      }
      // no parent z-index information, local element exists → replace in place
    } else if (local) {
      reconciledElements[local[1]] = remoteElement;
      localElementsData.set(remoteElement.id, [remoteElement, local[1]]);
      // otherwise push to the end
    } else {
      reconciledElements.push(remoteElement);
      localElementsData.set(remoteElement.id, [
        remoteElement,
        reconciledElements.length - 1 - offset,
      ]);
    }
  }
  const ret: readonly ExcalidrawElement[] = reconciledElements.filter(
    (element) => !duplicates.has(element),
  );
  return ret;
};

export const getUpdatedFiles = (
  localFiles: BinaryFiles,
  remoteFiles: BinaryFiles,
) =>
  Object.keys(remoteFiles).reduce<BinaryFiles>(
    (updatedFiles, currentFileId) => {
      if (
        !localFiles[currentFileId] ||
        (localFiles[currentFileId].lastRetrieved ?? 0) <
          (remoteFiles[currentFileId].lastRetrieved ?? 0)
      ) {
        // eslint-disable-next-line no-param-reassign
        updatedFiles[currentFileId] = remoteFiles[currentFileId];
      }
      return updatedFiles;
    },
    {},
  );
